import { useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import classNames from 'classnames'
import { createUseStyles } from 'react-jss'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { useParams } from 'react-router-dom'
import SmoothScroll from '@/components/SmoothScroll'
import Meta from '@/components/Meta'
import RevealFadeIn from '@/components/RevealFadeIn'
import ScrollDown from '@/components/ScrollDown'
import ProductVideo from '@/components/ProductVideo'
import ProductCoverMobile from '@/components/ProductCoverMobile'
import ProductToolbar from '@/components/ProductToolbar'
import ProductHero from '@/components/ProductHero'
import ProductOverview from '@/components/ProductOverview'
import ProductDescription from '@/components/ProductDescription'
import ProductGallery from '@/components/ProductGallery'
import ProductDownload from '@/components/ProductDownload'
import FooterProduct from '@/components/FooterProduct'
import usePagePrecache from '@/hooks/usePagePrecache'
import useMediaQuery from '@/hooks/useMediaQuery'
import * as contentActions from '@/actions/content'
import * as layoutActions from '@/actions/layout'
import * as localeActions from '@/actions/locale'
import { precacheMedia } from '@/utils/media'
import { mapRange } from '@/utils/math'
import style from './style'

const useStyles = createUseStyles(style)

const Product = ({ cpt: cptFromProps }) => {
  const classes = useStyles()
  const [isDataFetched, setDataFetched] = useState(false)
  const [isRotationFinished, setRotationFinished] = useState(false)
  const [isToolbarVisible, setToolbarVisible] = useState(false)
  const [activeSection, setCurrentSection] = useState('#overview')
  const [scrollSection, setScrollSection] = useState(-1)
  const [isBottomPage, setBottomPage] = useState(false)
  const [isOverviewInPage, setOverviewInPage] = useState(false)
  const [isOverviewExited, setOverviewExited] = useState(false)
  const [scaleVideo, setScaleVideo] = useState(1)
  const [loadNext, setLoadNext] = useState()
  const [nextData, setNextData] = useState({})
  const params = useParams()
  const stub = useRef(params.stub)
  const $root = useRef()
  const $overview = useRef(null)
  const $footer = useRef()
  const trigger = useRef([])
  const { match: isMobile } = useMediaQuery('sm')

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { page, strings, isPageAnimationReady } = useSelector((state) => ({
    page: state.content.cpt[cptFromProps]?.find((p) => p.slug === stub.current) || {},
    strings: state.options.strings,
    isPageAnimationReady: state.loading.isPageAnimationReady,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const fetchCpt = useCallback((slug) => dispatch(contentActions.fetchCpt(cptFromProps, slug)), [dispatch])
  const setLayoutStyle = useCallback((value) => dispatch(layoutActions.setLayoutStyle(value)), [dispatch])
  const setBackButtonProp = useCallback((key, value) => dispatch(layoutActions.setBackButtonProp(key, value)), [dispatch])
  const resetBackButtonProp = useCallback(() => dispatch(layoutActions.resetBackButtonProp()), [dispatch])
  const setTranslations = useCallback((content) => dispatch(localeActions.setTranslations(content)), [dispatch])

  /*------------------------------
  Inverse Header & Set back button visible
  ------------------------------*/
  useEffect(() => {
    setLayoutStyle('alternative')
    setBackButtonProp('visible', true)
    return () => {
      setLayoutStyle('default')
      resetBackButtonProp()
    }
  }, [])

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      setDataFetched(true)
      setTranslations(page)
      precacheMedia([{ type: 'video', src: page.acf?.overview.video_zoom }])

      setBackButtonProp('label', page.taxonomies['_cta_category'][0]?.name)
      setBackButtonProp('link', page.taxonomies['_cta_category'][0]?.link)
    }
  }, [page])

  /*------------------------------
  Preload
  ------------------------------*/
  const [load] = usePagePrecache({
    init: isDataFetched,
    sources: [
      { type: 'video', src: page.acf?.overview.video_rotation },
      { type: 'image', src: page.acf?.overview.video_rotation_poster.url },
    ],
    callback: () => {},
  })

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = useCallback(() => {
    if (Object.keys(page).length === 0) fetchCpt(stub.current)
  }, [page])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Manage loading next element
  ------------------------------*/
  useEffect(() => {
    if (loadNext) {
      fetchCpt(loadNext.slug)
        .then((data) => {
          setNextData(data)
        })
    }
  }, [loadNext])

  /*------------------------------
  Manage loading next element
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(nextData).length > 0) {
      precacheMedia([
        { type: 'video', src: nextData.acf?.overview?.video_rotation },
        { type: 'image', src: nextData.acf?.overview?.video_rotation_poster?.url },
        { type: 'video', src: nextData.acf?.overview?.video_zoom },
      ])
    }
  }, [nextData])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = useCallback(() => {
    return load && (
      <Meta
        title={page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }, [load, page])

  /*------------------------------
  Render Hero
  ------------------------------*/
  const renderVideo = useCallback(() => {
    return (
      <ProductVideo
        poster={page.acf.overview?.video_rotation_poster?.url}
        zoom={page.acf.overview.video_zoom}
        rotation={page.acf.overview.video_rotation}
        isRotationFinished={isRotationFinished}
        setRotationFinished={setRotationFinished}
        setToolbarVisible={setToolbarVisible}
        setScrollSection={setScrollSection}
        scrollSection={scrollSection}
        overviewSection={$overview}
        isOverviewInPage={isOverviewInPage}
        sectionsNumber={3}
        scaleVideo={scaleVideo}
        isOverviewExited={isOverviewExited}
        visible={isPageAnimationReady}
      />
    )
  }, [page, isRotationFinished, scrollSection, isOverviewInPage, scaleVideo, isOverviewExited, isPageAnimationReady])

  /*------------------------------
  Render Toolbar
  ------------------------------*/
  const renderToolbar = useCallback(() => {
    return (
      <ProductToolbar
        visible={isToolbarVisible && !isBottomPage}
        currentSection={activeSection}
        page={$root.current}
        items={[
          { title: strings.product_toolbar_label_1, anchor: '#overview' },
          { title: strings.product_toolbar_label_2, anchor: '#description' },
          { title: strings.product_toolbar_label_3, anchor: '#gallery' },
          { title: strings.product_toolbar_label_4, anchor: '#specifications' },
          { title: strings.product_toolbar_label_5, anchor: '#download' },
        ]}
      />
    )
  }, [isToolbarVisible, isBottomPage, activeSection, strings])

  /*------------------------------
  Render Overview Fixed Background
  ------------------------------*/
  const renderOverviewFixed = useCallback(() => {
    const sections = page.acf.overview.video_details || []
    return sections.length > 0 && (
      <div className={classes.overview}>
        <ProductHero
          title={page.title.rendered}
          subtitle={page.acf.subtitle}
          visible={isToolbarVisible && scrollSection === 0}
        />
        {sections
          .slice(0, 3)
          .map((detail, i) => (
            <ProductOverview
              key={i.toString()}
              title={detail.title}
              text={detail.text}
              visible={scrollSection === (i + 1)}
              align={detail.align}
              background={detail.background}
            />
          ))}
      </div>
    )
  }, [page, isToolbarVisible, scrollSection])

  /*------------------------------
  Render Overview
  ------------------------------*/
  const renderOverview = useCallback(() => {
    return (
      <div
        id="overview"
        className={`section ${classes.pageScrollOverview}`}
        ref={$overview}
      />
    )
  }, [])

  const handleClickScrollDown = useCallback(() => {
    setScrollSection(1)
  }, [])

  /*------------------------------
  Render Scroll Down
  ------------------------------*/
  const renderScrollDown = useCallback(() => {
    return (
      <RevealFadeIn
        visible={isToolbarVisible && scrollSection === 0}
        enterDelay={0.2}
        exitDuration={0.8}
        className={classes.overviewFooter}
      >
        <ScrollDown
          className={classNames({
            [classes.scrollDown]: true,
            [classes.hide]: !(isToolbarVisible && scrollSection === 0),
          })}
          color="dark"
          onClick={handleClickScrollDown}
        />
      </RevealFadeIn>
    )
  }, [isToolbarVisible, scrollSection])

  /*------------------------------
  Render Cover Mobile
  ------------------------------*/
  const renderCoverMobile = useCallback(() => {
    const sections = page.acf.overview.video_details || []
    return (
      <ProductCoverMobile
        cover={page.acf.mobile?.image.url}
        overview={sections}
        title={page.title.rendered}
        subtitle={page.acf.subtitle}
        visible={isPageAnimationReady}
      />
    )
  }, [page, isPageAnimationReady])

  /*------------------------------
  Render Description
  ------------------------------*/
  const renderDescription = useCallback(() => {
    return page.acf.description?.list.length > 0 && (
      <div
        className={`section ${classes.section} ${classes.description}`}
        id="description"
      >
        <ProductDescription
          items={page.acf.description?.list}
          image={page.acf.description?.description_image}
        />
      </div>
    )
  }, [page.slug])

  /*------------------------------
  Render Gallery
  ------------------------------*/
  const renderGallery = useCallback(() => {
    return page.acf.gallery?.free_gallery.length > 0 && (
      <div
        className={`section ${classes.section}`}
        id="gallery"
      >
        <ProductGallery
          className={classes.gallery}
          items={page.acf.gallery.free_gallery}
        />
      </div>
    )
  }, [page.slug])

  /*------------------------------
  Render Specifications
  ------------------------------*/
  const renderSpecifications = useCallback(() => {
    return page.acf.specifications?.specifications_list.length > 0 && (
      <div
        className={`section ${classes.section} ${classes.specifications}`}
        id="specifications"
      >
        <ProductDescription
          items={page.acf.specifications.specifications_list}
          image={page.acf.specifications.specifications_image}
          alignText="right"
        />
      </div>
    )
  }, [page.slug])

  /*------------------------------
  Render BottomPage
  ------------------------------*/
  const renderBottomPage = useCallback(() => {
    return page.acf.download?.file && (
      <div
        className={`section ${classes.wrapper} ${classes.section} ${classes.bottomPage}`}
        id="download"
      >
        <ProductDownload
          className={classes.download}
          data={{
            title: strings.product_download_title,
            text: strings.product_download_text,
            label: `${page.title.rendered} - ${strings.product_download_label}`,
            file: page.acf.download.file,
            name: page.title.rendered,
          }}
        />
      </div>
    )
  }, [page.slug, strings.product_download_title])

  /*------------------------------
  Handle Footer Mouse Enter
  ------------------------------*/
  const handleFooterMouseEnter = useCallback((next) => {
    if (!loadNext) setLoadNext(next)
  }, [loadNext])

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = useCallback(() => {
    return page.siblings.next && Object.keys(page.siblings.next).length > 0 && (
      <div ref={$footer}>
        <FooterProduct
          className={classes.footer}
          link={page.siblings.next.link}
          title={page.siblings.next.title}
          subtitle={page.siblings.next.subtitle}
          label={strings.next_product_label}
          onMouseEnter={() => handleFooterMouseEnter(page.siblings.next)}
          uppercase
          visible={!isMobile ? isBottomPage : isPageAnimationReady}
        />
      </div>
    )
  }, [page, strings, isBottomPage, isPageAnimationReady, isMobile])

  /*------------------------------
  ScrollTrigger Initialize
  ------------------------------*/
  useEffect(() => {
    if ($root.current && isToolbarVisible) {
      // Set 0 to animate-in the hero
      setScrollSection(0)
      // Overview Trigger
      if ($overview.current) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $overview.current,
          scroller: $root.current.ref,
          start: 'top center',
          end: 'bottom center',
          once: false,
          onToggle: (self) => setOverviewInPage(self.isActive),
          onUpdate: (self) => setScaleVideo(mapRange(self.progress.toFixed(1), 0.5, 1.0, 1, 1.3)),
        }))
        trigger.current.push(ScrollTrigger.create({
          trigger: $overview.current,
          scroller: $root.current.ref,
          start: 'top bottom',
          end: 'bottom top',
          once: false,
          onToggle: (self) => setOverviewExited(!self.isActive),
        }))
      }
      // Footer Trigger
      if ($footer.current) {
        trigger.current.push(ScrollTrigger.create({
          trigger: $footer.current,
          scroller: $root.current.ref,
          once: false,
          onToggle: (self) => setBottomPage(self.isActive),
        }))
      }
      // Section Trigger for active status Toolbar
      trigger.current.push(ScrollTrigger.batch('.section', {
        scroller: $root.current.ref,
        start: 'top center',
        end: 'bottom center',
        batchMax: 1,
        once: false,
        onEnter: (self) => setCurrentSection(`#${self[0].id}`),
        onEnterBack: (self) => setCurrentSection(`#${self[0].id}`),
      }))
    }
    return () => {
      if (isToolbarVisible && trigger.current.length > 0) {
        trigger.current.map((tr) => {
          if (Array.isArray(tr)) {
            tr.map((t) => t.kill())
          } else {
            tr.kill()
          }
          return false
        })
      }
    }
  }, [isToolbarVisible])

  return (
    <>
      {load && renderToolbar()}
      {(load && !isMobile) && renderVideo()}
      {(load && !isMobile) && renderOverviewFixed()}
      <SmoothScroll
        init={load}
        className={`page pageProduct ${classes.root}`}
        ref={$root}
      >
        {renderHelmet()}
        {load && (
          <>
            {!isMobile && renderOverview()}
            {!isMobile && renderScrollDown()}
            {isMobile && renderCoverMobile()}
            <div className={classes.backgroundBlue}>
              {renderDescription()}
              {renderGallery()}
              {renderSpecifications()}
            </div>
            <div className={classes.backgroundLight}>
              {renderBottomPage()}
              {renderFooter()}
            </div>
          </>
        )}
      </SmoothScroll>
    </>
  )
}

export default Product
